import { useEffect, useRef, useState, type ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import {
  Activity,
  Boxes,
  CalendarDays,
  ChevronDown,
  CircleDollarSign,
  Command,
  FileClock,
  Gauge,
  HeartPulse,
  LayoutDashboard,
  LogOut,
  Menu,
  MessageSquareText,
  PanelLeftClose,
  PanelLeftOpen,
  Search,
  Settings2,
  Users,
  X,
} from "lucide-react";
import { gsap } from "gsap";
import { useGSAP } from "@gsap/react";
import { api } from "./api";
import type { Paciente, Usuario } from "./types";
import { IconButton, StatusBadge } from "./components";
import { formatDate } from "./format";

gsap.registerPlugin(useGSAP);

const navigation = [
  { path: "/", label: "Início", icon: LayoutDashboard },
  {
    path: "/pacientes",
    label: "Pacientes",
    icon: Users,
    permission: "pacientes.visualizar",
  },
  {
    path: "/agenda",
    label: "Agenda",
    icon: CalendarDays,
    permission: "agenda.alterar",
  },
  {
    path: "/sessoes",
    label: "Sessões",
    icon: HeartPulse,
    permission: "sessoes.operar",
  },
  {
    path: "/estoque",
    label: "Estoque",
    icon: Boxes,
    permission: "estoque.movimentar",
  },
  {
    path: "/faturamento",
    label: "Faturamento",
    icon: CircleDollarSign,
    permission: "faturamento.fechar",
  },
  {
    path: "/comunicacao",
    label: "Comunicação",
    icon: MessageSquareText,
    permission: "comunicacao.enviar",
  },
  {
    path: "/indicadores",
    label: "Indicadores",
    icon: Gauge,
    permission: "indicadores.visualizar",
  },
  {
    path: "/auditoria",
    label: "Auditoria",
    icon: FileClock,
    permission: "auditoria.visualizar",
  },
  {
    path: "/administracao",
    label: "Administração",
    icon: Settings2,
    permission: "administracao.usuarios",
  },
];

interface SearchResult {
  pacientes: Pick<
    Paciente,
    | "id_paciente"
    | "nome_paciente"
    | "codigo_paciente"
    | "data_nascimento"
    | "status_paciente"
  >[];
  telas: { nome_tela: string; rota_tela: string }[];
}

export default function Shell({
  user,
  onLogout,
  children,
}: {
  user: Usuario;
  onLogout: () => void;
  children: ReactNode;
}) {
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileViewport, setMobileViewport] = useState(() => window.matchMedia("(max-width: 760px)").matches);
  const [queryText, setQueryText] = useState("");
  const [profileOpen, setProfileOpen] = useState(false);
  const [online, setOnline] = useState(navigator.onLine);
  const pageRef = useRef<HTMLDivElement>(null);
  const sidebarRef = useRef<HTMLElement>(null);
  const location = useLocation();
  const navigate = useNavigate();
  const search = useQuery({
    queryKey: ["busca-global", queryText],
    queryFn: () =>
      api<SearchResult>(`/busca?q=${encodeURIComponent(queryText)}`),
    enabled: queryText.trim().length >= 2,
  });

  useEffect(() => {
    const update = () => setOnline(navigator.onLine);
    window.addEventListener("online", update);
    window.addEventListener("offline", update);
    return () => {
      window.removeEventListener("online", update);
      window.removeEventListener("offline", update);
    };
  }, []);

  useEffect(() => {
    const query = window.matchMedia("(max-width: 760px)");
    const update = () => setMobileViewport(query.matches);
    query.addEventListener("change", update);
    return () => query.removeEventListener("change", update);
  }, []);

  useEffect(() => {
    if (!mobileViewport || !mobileOpen || !sidebarRef.current) return;
    const opener = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const sidebar = sidebarRef.current;
    const focusables = () => Array.from(sidebar.querySelectorAll<HTMLElement>('a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])'));
    focusables()[0]?.focus();
    const handleKeydown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        setMobileOpen(false);
        return;
      }
      if (event.key !== "Tab") return;
      const items = focusables();
      if (!items.length) return;
      const first = items[0];
      const last = items[items.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener("keydown", handleKeydown);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", handleKeydown);
      document.body.style.overflow = previousOverflow;
      opener?.focus();
    };
  }, [mobileOpen, mobileViewport]);

  useEffect(() => {
    setMobileOpen(false);
    setQueryText("");
  }, [location.pathname]);

  useGSAP(
    () => {
      if (
        !pageRef.current ||
        window.matchMedia("(prefers-reduced-motion: reduce)").matches
      )
        return;
      gsap.fromTo(
        pageRef.current,
        { y: 8, autoAlpha: 0.82 },
        { y: 0, autoAlpha: 1, duration: 0.26, ease: "power3.out" },
      );
    },
    { dependencies: [location.pathname], scope: pageRef, revertOnUpdate: true },
  );

  const availableNavigation = navigation.filter(
    (item) => !item.permission || user.permissoes.includes(item.permission),
  );

  function chooseResult(path: string) {
    setQueryText("");
    navigate(path);
  }

  return (
    <div
      className={`app-shell ${collapsed ? "app-shell--collapsed" : ""} ${mobileOpen ? "app-shell--mobile-open" : ""}`}
    >
      <aside ref={sidebarRef} id="primary-navigation" className="sidebar" aria-label="Navegação principal" aria-hidden={mobileViewport && !mobileOpen ? true : undefined} inert={mobileViewport && !mobileOpen ? true : undefined}>
        <div className="sidebar__brand">
          <Link to="/" aria-label="Ir para o início">
            <img src="/automyx-wordmark-oficial.png" alt="Automyx" />
          </Link>
          <IconButton
            label="Fechar menu"
            className="sidebar__mobile-close"
            onClick={() => setMobileOpen(false)}
          >
            <X size={19} />
          </IconButton>
        </div>
        <nav className="sidebar__nav">
          {availableNavigation.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              end={item.path === "/"}
              title={collapsed ? item.label : undefined}
            >
              <item.icon size={19} strokeWidth={1.8} />
              <span>{item.label}</span>
            </NavLink>
          ))}
        </nav>
        <div className="sidebar__footer">
          <div className="unit-context">
            <Activity size={18} />
            <span>
              <small>Unidade em operação</small>
              <strong>{user.nome_unidade}</strong>
            </span>
            <StatusBadge status={online ? "ATIVO" : "ATENCAO"} />
          </div>
          <button
            className="sidebar__collapse"
            type="button"
            onClick={() => setCollapsed((value) => !value)}
            aria-label={collapsed ? "Expandir menu lateral" : "Recolher menu lateral"}
            title={collapsed ? "Expandir menu" : "Recolher menu"}
          >
            {collapsed ? (
              <PanelLeftOpen size={18} />
            ) : (
              <PanelLeftClose size={18} />
            )}
            <span>{collapsed ? "Expandir" : "Recolher menu"}</span>
          </button>
        </div>
      </aside>
      {mobileOpen ? (
        <button
          className="sidebar-scrim"
          type="button"
          aria-label="Fechar menu"
          onClick={() => setMobileOpen(false)}
        />
      ) : null}
      <div className="workspace">
        <header className="topbar">
          <IconButton
            label="Abrir menu"
            className="topbar__menu"
            aria-controls="primary-navigation"
            aria-expanded={mobileOpen}
            onClick={() => {
              setCollapsed(false);
              setMobileOpen(true);
            }}
          >
            <Menu size={20} />
          </IconButton>
          <div className="global-search">
            <Search size={18} aria-hidden="true" />
            <input
              aria-label="Busca global"
              value={queryText}
              onChange={(event) => setQueryText(event.target.value)}
              placeholder="Buscar paciente ou tela…"
            />
            <kbd>
              <Command size={12} /> K
            </kbd>
            {queryText.trim().length >= 2 ? (
              <div className="global-search__results">
                {search.isLoading ? <p>Buscando registros…</p> : null}
                {search.data?.pacientes.map((patient) => (
                  <button
                    key={patient.id_paciente}
                    type="button"
                    onClick={() =>
                      chooseResult(`/pacientes?abrir=${patient.id_paciente}`)
                    }
                  >
                    <Users size={17} />
                    <span>
                      <strong>{patient.nome_paciente}</strong>
                      <small>
                        {patient.codigo_paciente} ·{" "}
                        {formatDate(patient.data_nascimento)}
                      </small>
                    </span>
                    <StatusBadge status={patient.status_paciente} />
                  </button>
                ))}
                {search.data?.telas.map((screen) => (
                  <button
                    key={screen.rota_tela}
                    type="button"
                    onClick={() => chooseResult(screen.rota_tela)}
                  >
                    <Search size={17} />
                    <span>
                      <strong>{screen.nome_tela}</strong>
                      <small>Abrir tela</small>
                    </span>
                  </button>
                ))}
                {!search.isLoading &&
                !search.data?.pacientes.length &&
                !search.data?.telas.length ? (
                  <p>Nenhum resultado encontrado.</p>
                ) : null}
              </div>
            ) : null}
          </div>
          <div className="topbar__actions">
            <div
              className={`sync-state ${online ? "" : "sync-state--offline"}`}
              title={
                online
                  ? "Conexão disponível"
                  : "Sem conexão; alterações bloqueadas"
              }
            >
              <span />
              {online ? "Online" : "Sem conexão"}
            </div>
            <div className="profile-menu">
              <button
                type="button"
                className="profile-menu__trigger"
                onClick={() => setProfileOpen((value) => !value)}
                aria-expanded={profileOpen}
              >
                <span className="avatar">
                  {user.nome_usuario
                    .split(" ")
                    .map((part) => part[0])
                    .slice(0, 2)
                    .join("")}
                </span>
                <span>
                  <strong>{user.nome_usuario}</strong>
                  <small>
                    {user.funcoes[0]?.toLocaleLowerCase("pt-BR") || "usuário"}
                  </small>
                </span>
                <ChevronDown size={15} />
              </button>
              {profileOpen ? (
                <div className="profile-menu__dropdown">
                  <p>
                    <strong>{user.nome_usuario}</strong>
                    <span>{user.email_usuario}</span>
                  </p>
                  <button type="button" onClick={onLogout}>
                    <LogOut size={17} />
                    Sair da central
                  </button>
                </div>
              ) : null}
            </div>
          </div>
        </header>
        <main className="page-frame" ref={pageRef}>
          {children}
        </main>
      </div>
    </div>
  );
}
